import type { CodeBlockType, ControlCharsType } from "./controlChars.js";
import type { Modifier, moveByOptions } from "./modifiers.js";
import type { QpFile, ViewStack } from "../gql/generated/schema.js";
import type {
  SDK_Box,
  SDK_Bullet,
  SDK_Colors,
  SDK_Filter,
  SDK_FontStyle,
  SDK_FontWeight,
  SDK_Fonts,
  SDK_Frame,
  SDK_GetFilesQuery,
  SDK_Grid,
  SDK_ImageFilter,
  SDK_Line,
  SDK_Link,
  SDK_List,
  SDK_QpFile,
  SDK_Read,
  SDK_Stack,
  SDK_StartingPoint,
  SDK_SubTitle,
  SDK_SuperTitle,
  SDK_Title
} from "../gql/generated/operations.js";

import type { KeywordsType } from "./keywords.js";
import type { ParsedStack } from "vitest";
import type { strings } from "./strings";

export function isAcceptedAudioFile(value: unknown): value is typeof AcceptedAudioFiles[number] {
  return AcceptedAudioFiles.includes(value as typeof AcceptedAudioFiles[number]);
}

export function isAcceptedImage(value: unknown): value is typeof AcceptedImages[number] {
  return AcceptedImages.includes(value as typeof AcceptedImages[number]);
}

export function isAcceptedDataFile(value: unknown): value is typeof AcceptedDataFiles[number] {
  return AcceptedDataFiles.includes(value as typeof AcceptedDataFiles[number]);
}


export const AcceptedAudioFiles = [
  "audio/mpeg",
  "audio/ogg",
  "audio/mp4"
] as const;

export const AcceptedImages = [
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/svg+xml"
] as const;

export const AcceptedDataFiles = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheet.sheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "application/json"
] as const;

export const AcceptedUploadFileTypes = [
  ...AcceptedDataFiles, ...AcceptedImages, ...AcceptedAudioFiles
] as const;

export interface AddOwnerNameResponse {
  success: boolean;
  message?: string;
}


export interface AiPayLoad {
  image?: string;
  jadle?: string;
  prompt?: string;
  filterName?: string;
  isInPortrait?: boolean;
  filter?: SDK_Filter;
  availableImages?: { name: string; alt: string }[];
  locale?: string;
}

export interface AiResponse {
  jadle: string;
  explanation: string;
}

export type AllFilterElementChildTypes =
  | SDK_Box
  | SDK_Bullet
  | SDK_Frame
  | SDK_Grid
  | SDK_ImageFilter
  | SDK_Line
  | SDK_Link
  | SDK_Read
  | SDK_StartingPoint
  | SDK_FontStyle
  | SDK_SubTitle
  | SDK_SuperTitle
  | SDK_Title
  | SDK_FontWeight
  | SDK_List;

export interface CheckOwnerNameResponse {
  success: boolean;
  message?: string;
}

export type ErrorMessage =
  | string
  | {
    response: { errors: { message: string }[] };
  };

export interface FilterEntry {
  filterName: string;
  filter: string;
}

export interface FontEntry {
  fontName: string;
  fontData: string;
}

export interface GetOwnerNameResponse {
  success: boolean;
  owners: string[];
}

export type GoldStandardResult = {
  filter: string;
  layout: string;
  jadle: string;
  image: string;
};

export type InternalFile =
  & Omit<
    SDK_QpFile,
    | "fileType"
    | "cloudFlareName"
    | "ownerName"
    | "fileName"
    | "_typeName"
    | "uploadedOn"
    | "id"
    | "ownerId"
    | "stackId"
    | "tags"
  >
  & {
    __typename?: "ViewFile";
    cloudFlareName?: string;
    fileName?: string;
    fileType?: string;
    id?: string;
    ownerId?: string;
    ownerName?: string;
    stackId?: string;
    uploadedOn?: number;
    tags?: string[];
    altText: string;
    url: string;
    userDefinedName: string;
  };


export interface LineObject {
  type: KeywordsType | ControlCharsType | CodeBlockType;
  content: string;
  modifier: Modifier;
  lineNumber: number
}

export interface Message {
  type: "QP" | "preview" | "fileUploader" | "test";
  action: MessageType;
  hostId: string,
  payload?:
  | boolean
  | FileList
  | number
  | QpFile
  | SDK_Colors
  | SDK_Filter
  | SDK_Fonts
  | SDK_GetFilesQuery["getFiles"]
  | SDK_Stack
  | string
  | string[]
  | ParsedStack
  | NavigationTypeType
  | { code: string; colors: SDK_Colors }
  | { stackCfKey: string; pageId: string }
  | { lineNumber: number; pageIndex: number; elementType: string; content: string };
  error?: ErrorMessage;
  ownerName?: string;
}




export type ParsedQpStack = Array<LineObject[]>;

export interface UploadFileMessage extends Message {
  ownerId: string;
  stackId: string;
}

export type MessageType =
  | typeof strings.messages.allFiles
  | typeof strings.messages.authorIsReady
  | typeof strings.messages.applicationError
  | typeof strings.messages.deleteStack
  | typeof strings.messages.deleteImage
  | typeof strings.messages.deleteImageFailed
  | typeof strings.messages.edit
  | typeof strings.messages.error
  | typeof strings.messages.fileUpload
  | typeof strings.messages.filtersAreLoaded
  | typeof strings.messages.ready
  | typeof strings.messages.page
  | typeof strings.messages.goToPage
  | typeof strings.messages.init
  | typeof strings.messages.injectFilter
  | typeof strings.messages.preview
  | typeof strings.messages.exitPresentationMode
  | typeof strings.messages.exitReadMode
  | typeof strings.messages.enterReadMode
  | typeof strings.messages.parsedStack
  | typeof strings.messages.presentationMode
  | typeof strings.messages.previewSnapDone
  | typeof strings.messages.restartPreview
  | typeof strings.messages.requestAllFiles
  | typeof strings.messages.requestFilterNames
  | typeof strings.messages.requestPreviewSnap
  | typeof strings.messages.requestStack
  | typeof strings.messages.saveStack
  | typeof strings.messages.setFilterColors
  | typeof strings.messages.setFilterFonts
  | typeof strings.messages.setFilterNames
  | typeof strings.messages.setStoryNames
  | typeof strings.messages.toggleStoriesDuringDemoMode
  | typeof strings.messages.test
  | typeof strings.messages.toggleFilesPanel
  | typeof strings.messages.toggleSharePanel
  | typeof strings.messages.toggleStacksPanel
  | typeof strings.messages.setNavigationType
  | typeof strings.messages.unPreview
  | typeof strings.messages.updateFiles
  | typeof strings.messages.getCodeAndFilter
  | typeof strings.messages.codeAndFilter
  | typeof strings.messages.code
  | typeof strings.messages.elementClick;

export type NavigationTypeType = typeof moveByOptions[number]

export interface CloudFlareResult {
  result: {
    filename: string;
    id: string;
    requireSignedURLs: boolean | string;
    uploaded: string;
  };
  url: string;
  success: boolean;
}

export interface OwnerPayload {
  owner?: string;
  jwt?: string;
  turnstileToken?: string;
}

export interface ProcessFileType extends SDK_QpFile {
  data: File;
  fileSize: number;
  fileType: typeof AcceptedUploadFileTypes[number];
  inUploadQ: boolean;
  uploadComplete: boolean;
  uploading: boolean;
  cloudFlarePromise?: Promise<CloudFlareResult>;
  cloudFlareResult?: CloudFlareResult;
}


export interface SsrData extends ViewStack {
  filterData: (FilterEntry | undefined)[];
}


export type UploadFileType = "audio" | "data" | "image" | "error";



export interface ViewStackMetaData {
  ownerId: string; // ownerId
  created: number; // created
  updated: number; // updated
  storyName: string
}


export type QPKvStack = { value: ViewStack | string; metadata: ViewStackMetaData };





